import type { Layout } from 'plotly.js';
import type { LegendPosition } from '@/store/figure-composition';

/**
 * Apply legend layout to a Plotly figure layout based on legend position.
 * 
 * @param layout - The Plotly layout object to modify
 * @param pos - The legend position (auto is treated as top_outside in multi-panel mode)
 * @param isMulti - Whether this is a multi-panel figure
 * @returns The modified layout object
 */
export function applyLegendLayout(
  layout: Partial<Layout>,
  pos: LegendPosition,
  isMulti: boolean
): Partial<Layout> {
  // In multi-panel mode, treat "auto" as "top_outside"
  const effective = isMulti && pos === "auto" ? "top_outside" : pos;

  // Start with a copy of the layout
  const updatedLayout = { ...layout };

  // Ensure margin exists
  if (!updatedLayout.margin) {
    updatedLayout.margin = { t: 60, r: 60, b: 60, l: 60 };
  } else {
    updatedLayout.margin = { ...updatedLayout.margin };
  }

  // Merge with existing legend properties, but prioritize our position settings
  const existingLegend = layout.legend || {};
  
  console.log('[applyLegendLayout] Applying legend layout:', { pos, effective, isMulti, existingLegend });
  
  switch (effective) {
    case "top_outside":
      updatedLayout.showlegend = true;
      // Create legend config, preserving existing orientation completely
      // Plotly paper coordinates: y=1 is top of plot area, y>1 is above plot area
      updatedLayout.legend = {
        ...existingLegend,
        // Position settings (always override)
        x: 0.5,
        xanchor: "center",
        y: 1.05, // Reduced from 1.5 to bring legend closer to plot
        yanchor: "bottom",
        // DO NOT override orientation - preserve whatever was set
        // Note: Plotly legend coordinates are in paper coordinates (0-1 range)
        // Values > 1.0 place the legend outside the plot area
      };
      // Add smaller top margin to reduce space
      const baseTopMargin = updatedLayout.margin.t ?? 60;
      updatedLayout.margin = {
        ...updatedLayout.margin,
        t: baseTopMargin + 40, // Reduced from +100 to +40
      };
      console.log('[applyLegendLayout] Top outside applied:', {
        legend: updatedLayout.legend,
        margin: updatedLayout.margin,
        baseTopMargin,
        finalTopMargin: updatedLayout.margin.t
      });
      break;

    case "bottom_outside":
      updatedLayout.showlegend = true;
      updatedLayout.legend = {
        ...existingLegend,
        // Position settings (always override)
        x: 0.5,
        xanchor: "center",
        y: -0.08, // Reduced from -0.25 to bring legend closer to plot
        yanchor: "top",
        // DO NOT override orientation - preserve whatever was set
        // Note: Plotly legend coordinates are already in paper coordinates (0-1 range)
        // Values < 0 place the legend outside the plot area
      };
      // Add smaller bottom margin to reduce space
      updatedLayout.margin = {
        ...updatedLayout.margin,
        b: (updatedLayout.margin.b ?? 60) + 40, // Reduced from +80 to +40
      };
      break;

    case "right_outside":
      updatedLayout.showlegend = true;
      updatedLayout.legend = {
        ...existingLegend,
        // Position settings (always override)
        x: 1.02, // Reduced from 1.08 to bring legend closer to plot
        xanchor: "left",
        y: 1,
        yanchor: "top",
        // DO NOT override orientation - preserve whatever was set
        // Note: Plotly legend coordinates are already in paper coordinates (0-1 range)
        // Values > 1.0 place the legend outside the plot area
      };
      // Add smaller right margin to reduce space
      updatedLayout.margin = {
        ...updatedLayout.margin,
        r: (updatedLayout.margin.r ?? 60) + 40, // Reduced from +80 to +40
      };
      break;

    case "none":
      updatedLayout.showlegend = false;
      break;

    case "auto":
      // In single-panel mode, keep existing behavior (don't modify legend)
      // The layout is returned as-is
      break;
  }

  return updatedLayout;
}

